import { useEffect, useRef } from "react"
import { getDayName, getDayNumber, getWeekDaysOfWeek } from "./utils"
import { IEnkCalendarViewProps } from "./calendar-types"
import { EnkCalendarDay } from "./calender-day"

export default function EnkCalendarWeekView(props: IEnkCalendarViewProps) {
  const { selectedDate, setSelectedDate, currentLocale } = props
  const container = useRef<HTMLDivElement>(null)
  const containerNav = useRef<HTMLDivElement>(null)
  const containerOffset = useRef<HTMLDivElement>(null)

  const datesOfWeek = getWeekDaysOfWeek(selectedDate)

  useEffect(() => {
    // Set the container scroll position based on the current time.
    const currentMinute = new Date().getHours() * 60
    setTimeout(() => {
      if (container.current && containerNav.current && containerOffset.current) {
        container.current.scrollTop =
          ((container.current.scrollHeight - containerNav.current.offsetHeight - containerOffset.current.offsetHeight) * currentMinute) / 1440
      }
    }, 100)
  }, [])

  const handleDayClick = (day: Date) => {
    setSelectedDate(day)
  }

  return (
    <div ref={container} className="isolate flex flex-auto flex-col overflow-auto bg-white">
      <div style={{ width: "165%" }} className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full">
        <div ref={containerNav} className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black ring-opacity-5 sm:pr-8">
          {/* Kleine weergave van dagen */}
          <div className="grid grid-cols-7 text-sm leading-6 text-gray-500 sm:hidden">
            {datesOfWeek.map((day) => (
              <button
                type="button"
                className="flex flex-col items-center pb-3 pt-2"
                onClick={() => {
                  handleDayClick(new Date(day.date))
                }}
              >
                {getDayName(currentLocale, day)}
                <EnkCalendarDay day={day} />
              </button>
            ))}
          </div>

          {/* Grote weergave van dagen */}
          <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm leading-6 text-gray-500 sm:grid">
            <div className="col-end-1 w-14" />

            {datesOfWeek.map((day) => (
              <div className="flex items-center justify-center py-3">
                <span className={day.isToday ? "flex items-baseline" : ""}>
                  {getDayName(currentLocale, day)}{" "}
                  <span
                    className={
                      day.isToday
                        ? "ml-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 font-semibold text-white"
                        : "items-center justify-center font-semibold text-gray-900"
                    }
                  >
                    {getDayNumber(day)}
                  </span>
                </span>
              </div>
            ))}
          </div>
          {/*einde van dagen grote weergave */}
        </div>

        {/* Calendar */}
        <div className="flex flex-auto">
          <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
          <div className="grid flex-auto grid-cols-1 grid-rows-1">
            {/* Horizontal lines */}
            <div
              className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
              style={{ gridTemplateRows: "repeat(48, minmax(3.5rem, 1fr))" }}
            >
              <div ref={containerOffset} className="row-end-1 h-7"></div>
              {/* 24 hours */}
              {[...Array(24).keys()].map((hour) => (
                <>
                  <div>
                    <div key={hour} className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                      {hour.toString().padStart(2, "0")}:00
                    </div>
                  </div>
                  <div />
                </>
              ))}
            </div>

            {/* Vertical lines */}
            <div className="col-start-1 col-end-2 row-start-1 hidden grid-cols-7 grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
              <div className="col-start-1 row-span-full" />
              <div className="col-start-2 row-span-full" />
              <div className="col-start-3 row-span-full" />
              <div className="col-start-4 row-span-full" />
              <div className="col-start-5 row-span-full" />
              <div className="col-start-6 row-span-full" />
              <div className="col-start-7 row-span-full" />
              <div className="col-start-8 row-span-full w-8" />
            </div>

            {/* Events */}
            <ol
              className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8"
              style={{ gridTemplateRows: "1.75rem repeat(288, minmax(0, 1fr)) auto" }}
            >
              <li className="relative mt-px flex sm:col-start-3" style={{ gridRow: "74 / span 12" }}>
                <a
                  href="#"
                  className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-blue-50 p-2 text-xs leading-5 hover:bg-blue-100"
                >
                  <p className="order-1 font-semibold text-blue-700">Breakfast</p>
                  <p className="text-blue-500 group-hover:text-blue-700">
                    <time dateTime="2022-01-12T06:00">6:00 AM</time>
                  </p>
                </a>
              </li>
              <li className="relative mt-px flex sm:col-start-3" style={{ gridRow: "92 / span 30" }}>
                <a
                  href="#"
                  className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-pink-50 p-2 text-xs leading-5 hover:bg-pink-100"
                >
                  <p className="order-1 font-semibold text-pink-700">Flight to Paris</p>
                  <p className="text-pink-500 group-hover:text-pink-700">
                    <time dateTime="2022-01-12T07:30">7:30 AM</time>
                  </p>
                </a>
              </li>
              <li className="relative mt-px hidden sm:col-start-6 sm:flex" style={{ gridRow: "122 / span 24" }}>
                <a
                  href="#"
                  className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-gray-100 p-2 text-xs leading-5 hover:bg-gray-200"
                >
                  <p className="order-1 font-semibold text-gray-700">Meeting with design team at Disney</p>
                  <p className="text-gray-500 group-hover:text-gray-700">
                    <time dateTime="2022-01-15T10:00">10:00 AM</time>
                  </p>
                </a>
              </li>
            </ol>
          </div>
        </div>

        {/* einde van calendar*/}
      </div>
    </div>
  )
}
